import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const client = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
});

export const SESSION_EXPIRED_EVENT = "lc-session-expired";
export const DOCTOR_SESSION_EXPIRED_EVENT = "lc-doctor-session-expired";
client.interceptors.response.use(
  (r) => r,
  (err) => {
    const detail = err?.response?.data?.detail;
    if (err?.response?.status === 401 && err.config?.headers?.["x-client-token"] &&
        ["Session expired", "Session revoked", "Invalid token"].includes(detail)) {
      window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT, { detail }));
    }
    if (err?.response?.status === 401 && err.config?.headers?.["x-doctor-token"]) {
      window.dispatchEvent(new CustomEvent(DOCTOR_SESSION_EXPIRED_EVENT, { detail }));
    }
    return Promise.reject(err);
  }
);

// Public
export const createBooking = (payload) =>
  client.post("/bookings", payload).then((r) => r.data);

export const createQuote = (payload) =>
  client.post("/quotes", payload).then((r) => r.data);

// Admin (requires PIN header)
const adminHeaders = (pin) => ({ headers: { "x-admin-pin": pin } });

export const verifyAdmin = (pin) =>
  client.post("/admin/verify", {}, adminHeaders(pin)).then((r) => r.data);

export const downloadMobileApiSpec = async (pin) => {
  const r = await client.get("/admin/docs/mobile-api", { ...adminHeaders(pin), responseType: "blob" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(r.data);
  a.download = "LONGEV_MOBILE_API.md";
  a.click();
  URL.revokeObjectURL(a.href);
};

export const listBookings = (pin) =>
  client.get("/bookings", adminHeaders(pin)).then((r) => r.data);

export const listQuotes = (pin) =>
  client.get("/quotes", adminHeaders(pin)).then((r) => r.data);

export const updateBookingStatus = (pin, id, status) =>
  client
    .patch(`/bookings/${id}`, { status }, adminHeaders(pin))
    .then((r) => r.data);

export const updateQuoteStatus = (pin, id, status) =>
  client
    .patch(`/quotes/${id}`, { status }, adminHeaders(pin))
    .then((r) => r.data);

export const fetchStats = (pin) =>
  client.get("/stats", adminHeaders(pin)).then((r) => r.data);

// Assessment (public + admin)
export const createAssessment = (payload) =>
  client.post("/assessments", payload).then((r) => r.data);

export const listAssessments = (pin) =>
  client.get("/assessments", adminHeaders(pin)).then((r) => r.data);

export const updateAssessmentStatus = (pin, id, status) =>
  client
    .patch(`/assessments/${id}`, { status }, adminHeaders(pin))
    .then((r) => r.data);

// ---------- Clients (admin) ----------
export const listClients = (pin) =>
  client.get("/clients", adminHeaders(pin)).then((r) => r.data);

export const createClient = (pin, payload) =>
  client.post("/clients", payload, adminHeaders(pin)).then((r) => r.data);

export const updateClient = (pin, id, payload) =>
  client.patch(`/clients/${id}`, payload, adminHeaders(pin)).then((r) => r.data);

export const sendClientInvite = (pin, id) =>
  client.post(`/clients/${id}/invite`, {}, adminHeaders(pin)).then((r) => r.data);

export const deleteClient = (pin, id) =>
  client.delete(`/clients/${id}`, adminHeaders(pin)).then((r) => r.data);

export const getClientLogs = (pin, id, days = 180) =>
  client
    .get(`/clients/${id}/logs?days=${days}`, adminHeaders(pin))
    .then((r) => r.data);

export const exportClientCsvUrl = (id) =>
  `${API}/clients/${id}/export.csv`;

export const adminCreateDaily = (pin, id, payload) =>
  client.post(`/clients/${id}/daily`, payload, adminHeaders(pin)).then((r) => r.data);
export const adminCreateWeekly = (pin, id, payload) =>
  client.post(`/clients/${id}/weekly`, payload, adminHeaders(pin)).then((r) => r.data);
export const adminCreateMonthly = (pin, id, payload) =>
  client.post(`/clients/${id}/monthly`, payload, adminHeaders(pin)).then((r) => r.data);

// ---------- Client self-service ----------
const clientHeaders = (token) => ({ headers: { "x-client-token": token } });

export const clientLogin = (identifier, pin) =>
  client.post("/client-auth/login", { identifier, pin }).then((r) => r.data);

export const clientRefreshSession = (token) =>
  client.post("/client-auth/refresh", {}, clientHeaders(token)).then((r) => r.data);

export const clientLogoutAll = (token) =>
  client.post("/client-auth/logout-all", {}, clientHeaders(token)).then((r) => r.data);

export const clientMe = (token) =>
  client.get("/client-me", clientHeaders(token)).then((r) => r.data);

export const clientLogs = (token, days = 180) =>
  client.get(`/client-me/logs?days=${days}`, clientHeaders(token)).then((r) => r.data);

export const clientPostDaily = (token, payload) =>
  client.post("/client-me/daily", payload, clientHeaders(token)).then((r) => r.data);

export const clientPostWeekly = (token, payload) =>
  client.post("/client-me/weekly", payload, clientHeaders(token)).then((r) => r.data);

export const clientPostMonthly = (token, payload) =>
  client.post("/client-me/monthly", payload, clientHeaders(token)).then((r) => r.data);

export const clientAnalyzeFoodPhoto = (token, { image_base64, mime_type, meal, note }) =>
  client
    .post(
      "/client-me/food/analyze",
      { image_base64, mime_type, meal, note },
      { ...clientHeaders(token), timeout: 60000 }
    )
    .then((r) => r.data);

// ---------- Session scheduling ----------
export const adminListSessions = (pin, includePast = false) =>
  client
    .get(`/sessions${includePast ? "?include_past=true" : ""}`, adminHeaders(pin))
    .then((r) => r.data);

export const adminCreateSession = (pin, payload) =>
  client.post("/sessions", payload, adminHeaders(pin)).then((r) => r.data);

export const adminUpdateSession = (pin, id, payload) =>
  client.patch(`/sessions/${id}`, payload, adminHeaders(pin)).then((r) => r.data);

export const adminDeleteSession = (pin, id) =>
  client.delete(`/sessions/${id}`, adminHeaders(pin)).then((r) => r.data);

export const clientListSessions = (token) =>
  client.get("/client-me/sessions", clientHeaders(token)).then((r) => r.data);

export const clientRsvp = (token, id) =>
  client.post(`/client-me/sessions/${id}/rsvp`, {}, clientHeaders(token)).then((r) => r.data);

export const clientCancelRsvp = (token, id) =>
  client.delete(`/client-me/sessions/${id}/rsvp`, clientHeaders(token)).then((r) => r.data);

// ---------- Doctor accounts (admin) ----------
export const adminListDoctors = (pin) =>
  client.get("/doctors", adminHeaders(pin)).then((r) => r.data);

export const adminCreateDoctor = (pin, payload) =>
  client.post("/doctors", payload, adminHeaders(pin)).then((r) => r.data);

export const adminUpdateDoctor = (pin, id, payload) =>
  client.patch(`/doctors/${id}`, payload, adminHeaders(pin)).then((r) => r.data);

export const adminDeleteDoctor = (pin, id) =>
  client.delete(`/doctors/${id}`, adminHeaders(pin)).then((r) => r.data);

// ---------- Doctor portal self ----------
const doctorHeaders = (token) => ({ headers: { "x-doctor-token": token } });

export const doctorLogin = (email, password) =>
  client.post("/doctor-auth/login", { email, password }).then((r) => r.data);

export const doctorMe = (token) =>
  client.get("/doctor-me", doctorHeaders(token)).then((r) => r.data);

export const doctorClients = (token) =>
  client.get("/doctor-me/clients", doctorHeaders(token)).then((r) => r.data);

export const doctorListLabs = (token, clientId) =>
  client.get(`/doctor-me/clients/${clientId}/labs`, doctorHeaders(token)).then((r) => r.data);

export const doctorUploadLab = (token, clientId, { title, notes, file }, onProgress) => {
  const form = new FormData();
  form.append("title", title);
  if (notes) form.append("notes", notes);
  form.append("file", file);
  return client
    .post(`/doctor-me/clients/${clientId}/labs`, form, {
      ...doctorHeaders(token),
      headers: { ...doctorHeaders(token).headers, "Content-Type": "multipart/form-data" },
      timeout: 120000,
      onUploadProgress: onProgress,
    })
    .then((r) => r.data);
};

// ---------- Labs (client + admin views, download) ----------
export const clientListLabs = (token) =>
  client.get("/client-me/labs", clientHeaders(token)).then((r) => r.data);

export const adminListClientLabs = (pin, clientId) =>
  client.get(`/clients/${clientId}/labs`, adminHeaders(pin)).then((r) => r.data);

export const downloadLab = async ({ labId, adminPin, clientToken, doctorToken }) => {
  const headers = {};
  if (adminPin) headers["x-admin-pin"] = adminPin;
  if (clientToken) headers["x-client-token"] = clientToken;
  if (doctorToken) headers["x-doctor-token"] = doctorToken;
  const resp = await client.get(`/labs/${labId}/download`, { headers, responseType: "blob" });
  return resp.data;
};

// ---------- Lab audit log + comments ----------
export const labAuditLog = (labId, { adminPin, doctorToken }) => {
  const headers = {};
  if (adminPin) headers["x-admin-pin"] = adminPin;
  if (doctorToken) headers["x-doctor-token"] = doctorToken;
  return client.get(`/labs/${labId}/audit`, { headers }).then((r) => r.data);
};

export const listLabComments = (labId, { adminPin, clientToken, doctorToken }) => {
  const headers = {};
  if (adminPin) headers["x-admin-pin"] = adminPin;
  if (clientToken) headers["x-client-token"] = clientToken;
  if (doctorToken) headers["x-doctor-token"] = doctorToken;
  return client.get(`/labs/${labId}/comments`, { headers }).then((r) => r.data);
};

export const postLabComment = (labId, text, { adminPin, clientToken, doctorToken }) => {
  const headers = {};
  if (adminPin) headers["x-admin-pin"] = adminPin;
  if (clientToken) headers["x-client-token"] = clientToken;
  if (doctorToken) headers["x-doctor-token"] = doctorToken;
  return client.post(`/labs/${labId}/comments`, { text }, { headers }).then((r) => r.data);
};

// ---------- Plan completions (persisted) ----------
export const listPlanCompletions = (token, date) =>
  client
    .get(`/client-me/plan-completions${date ? `?date=${date}` : ""}`, clientHeaders(token))
    .then((r) => r.data);

export const togglePlanCompletion = (token, { activity_id, date, completed }) =>
  client
    .post(
      "/client-me/plan-completions",
      { activity_id, date, completed },
      clientHeaders(token)
    )
    .then((r) => r.data);

// ---------- Push notifications ----------
export const pushPublicKey = () => client.get("/push/public-key").then((r) => r.data.public_key);

export const pushSubscribe = (token, subscription) =>
  client.post("/push/subscribe", subscription, clientHeaders(token)).then((r) => r.data);

export const pushUnsubscribe = (token, subscription) =>
  client
    .delete("/push/subscribe", { ...clientHeaders(token), data: subscription })
    .then((r) => r.data);

export const getNotificationPrefs = (token) =>
  client.get("/client-me/notification-prefs", clientHeaders(token)).then((r) => r.data);

export const setNotificationPrefs = (token, prefs) =>
  client.put("/client-me/notification-prefs", prefs, clientHeaders(token)).then((r) => r.data);

export const coachHistory = (token) =>
  client.get("/client-me/coach/messages", clientHeaders(token)).then((r) => r.data);

export const coachChat = (token, message) =>
  client.post("/client-me/coach/chat", { message }, clientHeaders(token)).then((r) => r.data);

export const coachClear = (token) =>
  client.delete("/client-me/coach/messages", clientHeaders(token)).then((r) => r.data);

export const deleteMyAccount = (token, pin) =>
  client
    .post("/client-me/delete-account", { pin, confirm: "DELETE" }, clientHeaders(token))
    .then((r) => r.data);

export const updateClientSelf = (token, patch) =>
  client.put("/client-me/profile", patch, clientHeaders(token)).then((r) => r.data);
